import React from 'react';
import { Card, Typography } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import Api from '../../Network';
import MobileTable from '../../Component/MobileTable';

const { Text } = Typography;

interface RelatedCustomersTabProps {
  enabled: boolean;
  driverId: string;
}

interface RelatedCustomerRow {
  customer_id: number;
  customer_name: string;
  phone_number: string;
  total_invoices_amount: number;
  total_paid_amount: number;
  total_due_amount: number;
  total_receipts_amount: number;
}

const RelatedCustomersTab: React.FC<RelatedCustomersTabProps> = ({ enabled, driverId }) => {
  const { t, i18n } = useTranslation();

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['sales-analytics-related-customers', driverId],
    queryFn: async (): Promise<RelatedCustomerRow[]> => {
      const res = await Api.get<{ status?: boolean; data?: { rows?: RelatedCustomerRow[] } }>(
        'api/admin/dashboard/sales-analytics-related-customers',
        { params: { driver_id: driverId } }
      );
      if (!res?.status || !res.data?.rows) {
        throw new Error('Failed to load related customers');
      }
      return res.data.rows;
    },
    enabled: enabled && !!driverId,
  });

  const formatMoney = (value: number) =>
    `${Number(value || 0).toLocaleString(i18n.language, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })} ${t('common.SAR', 'SAR')}`;

  const columns = [
    {
      title: t('salesRepresentatives.relatedCustomers.columns.customerName', 'Customer'),
      dataIndex: 'customer_name',
      key: 'customer_name',
    },
    {
      title: t('salesRepresentatives.relatedCustomers.columns.phone', 'Phone'),
      dataIndex: 'phone_number',
      key: 'phone_number',
    },
    {
      title: t('salesRepresentatives.relatedCustomers.columns.totalInvoices', 'Total invoices'),
      dataIndex: 'total_invoices_amount',
      key: 'total_invoices_amount',
      align: 'right' as const,
      render: (value: number) => formatMoney(value),
    },
    // {
    //   title: t('salesRepresentatives.relatedCustomers.columns.totalPaid', 'Total paid'),
    //   dataIndex: 'total_paid_amount',
    //   key: 'total_paid_amount',
    //   align: 'right' as const,
    //   render: (value: number) => formatMoney(value),
    // },
    {
      title: t('salesRepresentatives.relatedCustomers.columns.totalDue', 'Total due'),
      dataIndex: 'total_due_amount',
      key: 'total_due_amount',
      align: 'right' as const,
      render: (value: number) => formatMoney(value),
    },
    // {
    //   title: t('salesRepresentatives.relatedCustomers.columns.totalReceipts', 'Total receipts'),
    //   dataIndex: 'total_receipts_amount',
    //   key: 'total_receipts_amount',
    //   align: 'right' as const,
    //   render: (value: number) => formatMoney(value),
    // },
  ];

  return (
    <Card>
      <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
        {t(
          'salesRepresentatives.relatedCustomers.description',
          'All customers linked to this sales representative with the same totals used in sales analytics.'
        )}
      </Text>
      <MobileTable
        rowKey="customer_id"
        columns={columns}
        dataSource={data || []}
        loading={isLoading || isFetching}
        pagination={{ pageSize: 20, showSizeChanger: true }}
        scroll={{ x: 1200 }}
      />
    </Card>
  );
};

export default RelatedCustomersTab;
